<template>
  <div>
    <DeleteModal modal-id="modal-test-delete" @deleteToggle="deleteItem" />

    <b-modal
      id="modal-test-edit"
      :no-close-on-backdrop="true"
      ok-only
      centered
      size="lg"
      :title="
        createMode
          ? $t('reception.Add Patient Test Group')
          : $t('reception.Edit Patient Test Group')
      "
      @ok="submitItem()"
    >
      <b-card-text>
        <b-row>
          <b-col md="">
            <AutoComplete
              v-model="EditForm.tests"
              :label="$t('lab.test')"
              :placeholder="$t('lab.test')"
              :multiple="true"
              param-query="search"
              :serializer="
                (val) => {
                  return {
                    test_id: val.id,
                    price: val.price,
                  };
                }
              "
              endpoint="test"
              text-title="name"
            />
          </b-col>
        </b-row>
      </b-card-text>
    </b-modal>

    <div>
      <!-- Add this warning message above your table -->
      <b-alert
        v-if="hasUnapprovedResults"
        variant="warning"
        show
        class="d-flex align-items-center mb-3"
      >
        <i class="fas fa-exclamation-triangle mr-2" />
        <div>
          <strong>Important:</strong> Some test results need approval from the
          lab before they can be viewed clearly. Unapproved results appear
          blurred.
        </div>
      </b-alert>

      <!-- Data Table  -->
      <div class="d-flex justify-content-between mt-4 mb-2">
        <h2>
          {{ $t("reception.Patient Test Group") }}
        </h2>
        <!-- <b-button
            variant="primary"
            @click="show(EditForm, 'create')"
            v-if="
              $store.state.app.permissions.includes('store-patientCardTest') &&
              !isHistory
            "
          >
            {{ $t("reception.Add Patient Test Group") }}
          </b-button> -->

        <div class="d-flex justify-content-end">
          <b-button
            v-if="!hideAddButton"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mb-75 bgcolor mx-2"
            :disabled="disabled"
            @click="
              $router.push({
                name: 'add-test',
                params: { id: patientCardId2 },
              })
            "
            >{{ $t("lab.add-patient-test") }}
          </b-button>
          <tests
            :disabled="disabled"
            :data="data"
            :patient-info="patientInfo"
          />
        </div>
      </div>

      <b-table
        v-if="!loading"
        :items="data"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        empty-text="No matching results found"
        class="position-relative"
      >
        <template #cell(price)="data">
          <span class="text-nowrap"> {{ data.item.price }} IQD </span>
        </template>
        <template #cell(normalRange)="data">
          <span
            >{{ getNestedProperty(data.item, "writeTest.min") }} -
            {{ getNestedProperty(data.item, "writeTest.max") }}
          </span>
        </template>

        <template #cell(result)="data">
          <span
            class="text-nowrap"
            :class="{ 'blur-unapproved': data.item.is_approved === 0 }"
          >
            {{ data.item.result }}
          </span>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <div class="text-nowrap">
            <!-- @click="printId(data.item.id)" -->

            <!-- Dropdown -->
            <ActionsButtons
              v-if="!isHistory"
              :no-edit="true"
              delete-perm="delete-patientCardTest"
              edit-perm="delete-patientCardTest"
              :disabled="disabled"
              @editToggle="show(data.item)"
              @deleteToggle="deleteModel(data.item)"
            />
            <!-- <b-dropdown
                variant="link"
                toggle-class="p-0"
                no-caret
                :right="$store.state.appConfig.isRTL"
              >
                <template #button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="align-middle text-body"
                  />
                </template>

                <b-dropdown-item
                  @click="deleteModel(data.item)"
                  v-if="
                    $store.state.app.permissions.includes(
                      'delete-patientCardTest'
                    )
                  "
                >
                  <feather-icon icon="TrashIcon" />
                  <span class="align-middle ml-50">Delete</span>
                </b-dropdown-item>
              </b-dropdown> -->
          </div>
        </template>
      </b-table>
      <template v-if="loading">
        <div>
          <div class="d-flex justify-content-center mb-1">
            <b-spinner label="Loading..." />
          </div>
        </div>
      </template>
      <div class="mx-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted"
              >{{ $t("common.showing") }} 1 {{ $t("common.to") }} 10
              {{ $t("common.patients") }}</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
            @click="getPatientTestGroupList()"
          >
            <b-pagination
              v-model="page"
              :total-rows="dataCount"
              :per-page="take"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BFormGroup,
  BButton,
  BCol,
  BRow,
  BFormInput,
  BTable,
  BDropdown,
  BDropdownItem,
  BPagination,
  BSpinner,
  BAlert,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import VuePhoneNumberInput from "vue-phone-number-input";
import "vue-phone-number-input/dist/vue-phone-number-input.css";
import permissionChecker from "@/mixins/permissionChecker";
import DeleteModal from "@/components/modals/DeleteModal.vue";
import ActionsButtons from "@/components/common/ActionsButtons.vue";
import Tests from "@/components/printables/Tests.vue";

const AutoComplete = () => import("@/components/common/AutoComplete.vue");

export default {
  components: {
    DeleteModal,
    Tests,
    ActionsButtons,
    BCard,
    BFormGroup,
    BButton,
    BCol,
    BRow,
    BFormInput,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    BAlert,
    VuePhoneNumberInput,
    AutoComplete,
  },
  mixins: [permissionChecker],

  props: [
    "patientCardId",
    "isHistory",
    "patientCardId2",
    "disabled",
    "showName",
    "hideAddButton",
  ],
  data() {
    return {
      take: 10,
      loading: true,
      isNotValid: false,
      dataCount: 0,
      data: [],
      EditForm: {
        patient_card_id: "",
        tests: [],
        _method: "PUT",
      },
      createMode: false,
      errorForm: {
        patient_card_id: "",
        tests: [],
      },
      params: {
        form: {},
        path: "",
      },
      tableColumns: [
        {
          key: "patient_card.patient.name",
          label: this.$t("common.patient-name"),
          sortable: true,
        },
        {
          key: "test.name",
          label: this.$t("lab.test"),
          sortable: true,
        },
        {
          key: "result",
          sortable: true,

          label: this.$t("lab.result"),
        },
        {
          key: "unit.name",
          sortable: true,
          label: this.$t("lab.unit"),
        },
        {
          key: "normalRange",
          sortable: true,

          label: this.$t("lab.normalRange"),
        },
        // {
        //   key: "result_as_to_normal_range",
        //   sortable: true,

        //   label: this.$t("lab.resultAsToNormalRange"),
        // },
        { key: "Actions", label: this.$t("common.actions") },
      ],
      currentPage: 1,
      takeOptions: [10, 25, 50, 100],
      searchQuery: "",
      sortBy: "id",
      isSortDirDesc: true,
      page: 1,
      patientInfo: {},
      writeTest: [],
      hasUnapprovedResults: false,
    };
  },
  computed: {
    hasUnapprovedResults() {
      return this.data && this.data.some((item) => item.is_approved === 0);
    },
  },
  mounted() {
    this.getPatientTestGroupList();
    this.getPatientsInfo();
    if (this.showName) {
      this.tableColumns.splice(0, 1);
    }
  },
  methods: {
    getNestedProperty(obj, path, defaultValue = "N/A") {
      const result = path
        .split(".")
        .reduce(
          (acc, part) => (acc && acc[part] ? acc[part] : defaultValue),
          obj
        );

      // console.log("result", result);
      return result;
    },
    show(item, mode) {
      if (mode === "create") {
        this.createMode = true;
        this.EditForm = {
          patient_card_id: this.patientCardId,
          tests: [],
          _method: "POST",
        };
      } else {
        this.createMode = false;

        this.EditForm.id = item.id;
        this.EditForm.patient_card_id = this.patientCardId;
        this.EditForm.tests = item.tests;

        this.EditForm._method = "PUT";
      }
      this.$bvModal.show("modal-test-edit");
    },
    showPhoneObj(obj, countryCallingCodeCheck) {
      this.isNotValid = !obj.isValid;
      countryCallingCodeCheck
        ? (this.countryCallingCodeFirst = obj.countryCallingCode)
        : (this.countryCallingCodeSecond = obj.countryCallingCode);
    },
    async getPatientsInfo() {
      const Id = this.$route.params.id;
      // console.log("Id", Id);
      await this.getPatient(`/patientCard/${this.patientCardId}`);
      for (const item in this.all_data) {
        if (this.all_data[item] == null) this.all_data[item] = "";
      }

      this.patientInfo = this.all_data;
      console.log("this.patientInfo", this.patientInfo);
      this.dataCount = this.all_data.total;
    },

    async getPatientTestGroupList() {
      this.loading = true;
      // check if there are any null value in the obj we sending

      let url = `/patientCardTest?filter[patient_card_id]=${this.patientCardId}&page=${this.page}&take=${this.take}&sort=-id&include=test,group,patientCard,unit`;

      if (this.testFilter) {
        url += `&filter[test_id]=${this.testFilter}`;
      }
      if (this.patientFilter) {
        url += `&filter[patient_card_id]=${this.patientFilter}`;
      }

      await this.getList(url);
      this.data = this.all_data.data;

      // Use the normal_range_min and normal_range_max that are already in the response
      // No need to make additional API calls
      this.data = this.data.map((item) => {
        // Create writeTest object from the existing normal_range_min and normal_range_max
        item.writeTest = {
          min: item.normal_range_min,
          max: item.normal_range_max,
        };
        return item;
      });

      this.dataCount = this.all_data.total;
      this.loading = false;

      // Check for unapproved results
      this.hasUnapprovedResults = this.data.some(
        (item) => item.is_approved === 0
      );
    },
    deleteModel(item) {
      this.deleteForm = item;
      this.$bvModal.show("modal-test-delete");
    },
    deleteItem() {
      this.$store
        .dispatch("delete", `/patientCardTest/${this.deleteForm.id}`)
        .then((response) => {
          this.getPatientTestGroupList();

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Delete Success",
              icon: "success",
              variant: "info",
              text: `deleted ${this.deleteForm.first_name} successfully`,
            },
          });
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              variant: "danger",
              text: `Error deleting ${this.deleteForm.first_name}`,
            },
          });
        });
    },
    submitItem() {
      for (const item in this.errorForm) {
        this.errorForm[item] = "";
        if (this.EditForm[item] == "" || this.EditForm[item] == null)
          delete this.EditForm[item];
      }

      if (this.createMode) {
        (this.params.form = this.EditForm),
          (this.params.path = "/patientCardTest"),
          this.$store
            .dispatch("create", this.params)
            .then((response) => {
              this.getPatientTestGroupList();
            })
            .catch((error) => {
              if (error.response.status === 400) {
                for (const item in error.response.data) {
                  this.$toast({
                    component: ToastificationContent,
                    position: "top-right",
                    props: {
                      title: "validation error",
                      variant: "danger",
                      text: error.response.data[item][0],
                    },
                  });
                }
              }
            });
      } else {
        (this.params.form = this.EditForm),
          (this.params.path = `/patientCardTest/${this.EditForm.id}`),
          this.$store
            .dispatch("update", this.params)
            .then((response) => {
              this.getPatientTestGroupList();
            })
            .catch((error) => {
              if (error.response.status === 400) {
                for (const item in error.response.data) {
                  this.$toast({
                    component: ToastificationContent,
                    position: "top-right",
                    props: {
                      title: "validation error",
                      variant: "danger",
                      text: error.response.data[item][0],
                    },
                  });
                }
              }
            });
      }
    },
  },
};
</script>
<style scoped>
/* CSS */
.button-preview {
  align-items: center;
  appearance: none;
  background-color: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 0.375em;
  box-shadow: none;
  box-sizing: border-box;
  color: #363636;
  cursor: pointer;
  display: inline-flex;
  font-family: BlinkMacSystemFont, -apple-system, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", Helvetica,
    Arial, sans-serif;
  font-size: 1rem;
  height: 2.5em;
  justify-content: center;
  line-height: 1.5;
  padding: calc(0.5em - 1px) 1em;
  position: relative;
  text-align: center;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
  vertical-align: top;
  white-space: nowrap;
  margin-bottom: 3px;
  margin-right: 10px;
}

.button-preview:active {
  border-color: #4a4a4a;
  outline: 0;
}

.button-preview:focus {
  border-color: #485fc7;
  outline: 0;
}

.button-preview:hover {
  border-color: #b5b5b5;
  background: rgb(245, 241, 241);
}

.button-preview:focus:not(:active) {
  box-shadow: rgba(72, 95, 199, 0.25) 0 0 0 0.125em;
}

.modify {
  height: 6rem;
  font-size: 2rem;
  align-items: center;
}

.modify:hover {
  box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
}

.blur-unapproved {
  filter: blur(3px);
  user-select: none;
  position: relative;
}

/* Optional: add a hover effect to show it's intentionally blurred */
.blur-unapproved:hover::after {
  content: "Pending approval";
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
